import { useAuth } from "@clerk/clerk-react";
import { Box, Grid, Skeleton, Typography } from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { SlideAnimationContext } from "contexts/SlideAnimation";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  OrganizationNiche,
  OrganizationProfile,
  OrganizationVaProfile,
} from "schemas/dashboard";
import { routes } from "schemas/routes";

import { fetcherAuth } from "utils/api";

import { SaveOrgVaProfileFunc, StepType } from "../schema";
import StepsToPickTemplate from "./components/StepsToPickTemplate";

interface Props {
  organizationVaProfile: OrganizationVaProfile | null;
  saveOrgVaProfile: SaveOrgVaProfileFunc;
  organizationProfile: OrganizationProfile | null;
  numsOfTemplate: number;
  loading: boolean;
}

const CreateYourOwn = ({
  organizationVaProfile,
  saveOrgVaProfile,
  organizationProfile,
  numsOfTemplate,
  loading,
}: Props) => {
  const { getToken } = useAuth();
  const { fetchOutreachTemplates, fetchOutreachTemplatesLoading, templates } =
    useContext(OutreachTemplatesContext);
  const { currentOrg } = useContext(OrganizationUserContext);

  const [steps, setSteps] = useState<StepType[]>([]);
  const [activeStep, setActiveStep] = useState(0);

  const location = useLocation();
  const navigate = useNavigate();
  const slideContext = useContext(SlideAnimationContext);

  const initializeSteps = async () => {
    const { niches } = await fetcherAuth(
      getToken,
      `/api/organization/${currentOrg?.id}/organization-niches`,
    );
    const sortedNiches =
      niches
        .filter((niche: OrganizationNiche) => !niche.needsTemplate)
        .sort(
          (x: OrganizationNiche, y: OrganizationNiche) =>
            Number(y.emailSettingId) - Number(x.emailSettingId),
        ) || [];
    const steps: StepType[] = [];
    let activeStep = 0;
    for (const niche of sortedNiches) {
      if (niche?.emailSettingId) {
        activeStep += 1;
      }
      steps.push({
        label: `Select ${niche.name} Templates`,
        description: "",
        niche,
      });
    }
    steps.push({
      label: "Select General Use Templates",
      description: `This is the default outreach template used when a brand doesn't fall into any of the specific niches listed above. For example, if you save a brand for your VA (Virtual Assistant) to contact and it doesn't match any of the predefined niches, this general template will automatically be used.`,
    });
    setSteps(steps);
    setActiveStep(activeStep);
  };

  const moveToNextPage = async () => {
    if (slideContext) {
      await slideContext.slideOutUp();
      slideContext.slideInUp();
    }
    if (numsOfTemplate > 0) {
      navigate(
        `/${routes.templatesCopyWriterSelfServeOnboarding}${location.search}`,
      );
    } else if (
      organizationProfile?.primarySocialMediaPlatform &&
      organizationProfile?.primarySocialMediaPlatform?.length > 0
    ) {
      navigate(`/${routes.faqSelfServeOnboarding}${location.search}`);
    } else {
      navigate(
        `/${routes.socialMediaLinksSelfServeOnboarding}${location.search}`,
      );
    }
  };

  useEffect(() => {
    if (currentOrg?.id) {
      initializeSteps();
    }
    if (currentOrg?.id && templates?.length === 0) {
      fetchOutreachTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  return (
    <>
      <Typography variant="h3" gutterBottom sx={{ textAlign: "center", mt: 0 }}>
        <Box
          component="i"
          className="fa-regular fa-pen"
          color="secondary.dark"
          sx={{ pr: 1 }}
        />{" "}
        Your Niche Templates
      </Typography>
      <Typography
        variant="subtitle1"
        gutterBottom
        sx={{ textAlign: "center", mb: 3 }}
      >
        Outreach templates help you reach out to brands by giving you a
        ready-made message. Pick the outreach templates for each niche below.
      </Typography>

      {fetchOutreachTemplatesLoading || loading ? (
        <Grid>
          <Skeleton animation="wave" width="100%" height="50px" />
          <Skeleton animation="wave" width="100%" height="50px" />
          <Skeleton
            animation="wave"
            width="100%"
            height="400px"
            sx={{ mt: -10, mb: -8 }}
          />
          <Skeleton animation="wave" width="100%" height="80px" />
        </Grid>
      ) : (
        <StepsToPickTemplate
          steps={steps}
          setSteps={setSteps}
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          organizationProfile={organizationProfile}
          organizationVaProfile={organizationVaProfile}
          saveOrgVaProfile={saveOrgVaProfile}
          moveToNextPage={moveToNextPage}
        />
      )}
    </>
  );
};

export default CreateYourOwn;
